import {
  bookAbbreviationsPerLanguage,
  type ParsedReference,
  parseReference,
  type StandaloneBookData,
} from "@rsc/scripture-model";
import type { ScriptureClientInterface } from "@rsc/scripture-client";
import { ResultAsync } from "neverthrow";
import { getScriptureErrorMessage } from "./util/errors";
import { createVidRange, type VidRange } from "./ranges";
import { formatBookName, formatVerseRange } from "./util/render";

/** Presentation state for a reference being typed; interpretation belongs to API v2. */
export interface ScriptureQuery {
  text: string | null;
  parsed: ParsedReference | null;
  range: VidRange | null;
  rendered: string | null;
  error: string | null;
  hint: string | null;
}

/**
 * Create plain reference-input state; omitted presentation and interpretation fields are null.
 */
export function createScriptureQuery(
  text: string | null = null,
  parsed: ParsedReference | null = null,
  range: VidRange | null = null,
  rendered: string | null = null,
  error: string | null = null,
  hint: string | null = null,
): ScriptureQuery {
  return { text, parsed, range, rendered, error, hint };
}

/**
 * Interpret typed text through API v2 and return presentation state containing the range or error.
 * Inputs shorter than two characters produce a hint without a request.
 * @param tr Explicit result revision to retain; omission lets the service choose the revision.
 */
export async function interpretScriptureQuery(
  client: ScriptureClientInterface,
  tid: string | null,
  localTid: string,
  text: string,
  tr?: string,
): Promise<ScriptureQuery> {
  const parsed = parseReference(text).unwrapOr(null);
  if (text.length < 2)
    return createScriptureQuery(
      text,
      null,
      null,
      null,
      null,
      "Type something.",
    );
  return client
    .interpret(text, localTid, tid ?? undefined, undefined, { resultTr: tr })
    .match(
      ({ range, rendered }) =>
        createScriptureQuery(
          text,
          parsed,
          createVidRange(range),
          rendered,
          null,
          rendered,
        ),
      (error) =>
        createScriptureQuery(
          text,
          parsed,
          null,
          null,
          getScriptureErrorMessage(error),
        ),
    );
}

/**
 * Render a locally parsed reference for server interpretation, preserving chapter-only ranges.
 */
export function renderParsedReference(parsed: ParsedReference): string {
  const { book, fromChapter, fromVerse, toChapter, toVerse } = parsed;
  if (!fromChapter) return book ?? "";
  if (!fromVerse)
    return `${book} ${fromChapter}${toChapter && toChapter !== fromChapter ? `–${toChapter}` : ""}`;
  return formatVerseRange(
    fromVerse,
    toVerse ?? fromVerse,
    book ?? "",
    String(fromChapter),
    String(toChapter ?? fromChapter),
  );
}

/**
 * Resolve a parsed reference through API v2, preserving typed client errors and the returned revision.
 * @param tr Optional explicit revision for the interpreted result.
 */
export function findVidRange(
  client: ScriptureClientInterface,
  tid: string | null,
  localTid: string,
  parsed: ParsedReference,
  tr?: string,
) {
  return client
    .interpret(
      renderParsedReference(parsed),
      localTid,
      tid ?? undefined,
      undefined,
      { resultTr: tr },
    )
    .map(({ range }) => createVidRange(range));
}

/**
 * Create a book matcher using normalized name prefixes or exact known abbreviations.
 * Matching ignores case, accents, common punctuation, and supported Roman numeral prefixes.
 */
export function getBookPredicate(query: string) {
  /**
   * Normalize a book query or name for accent-, punctuation-, and numeral-insensitive matching.
   */
  const normalize = (text: string) =>
    text
      .replace(/^III/i, "3")
      .replace(/^II/i, "2")
      .replace(/^I(?=\s)/i, "1")
      .normalize("NFD")
      .replace(/\p{M}/gu, "")
      .toLocaleLowerCase()
      .replace(/[\s.,;:!?]/g, "");
  const normalized = normalize(query);
  return (book: StandaloneBookData) =>
    normalize(book.name).startsWith(normalized) ||
    Object.values(bookAbbreviationsPerLanguage).some((books) =>
      books[book.bid].some(
        (abbreviation) => normalize(abbreviation) === normalized,
      ),
    );
}
/**
 * Return matching books in input order without mutating the source list.
 */
export function findBooks(books: StandaloneBookData[], query: string) {
  return books.filter(getBookPredicate(query));
}

/**
 * Fetch books in the selected and local translations and pair them by book ID.
 * Reuse the request when translations match; missing local books fall back to the selected
 * book. Client failures remain ResultAsync errors.
 */
export function getBooksInForeignAndLocalTranslation(
  client: ScriptureClientInterface,
  tid: string,
  localTid: string,
) {
  const foreign = client.getBooks(tid);
  return ResultAsync.combine([
    foreign,
    tid === localTid ? foreign : client.getBooks(localTid),
  ]).map(([books, localBooks]) =>
    books.map((book) => {
      const localBook =
        localBooks.find((local) => local.bid === book.bid) ?? book;
      return { book, localBook, name: formatBookName(book, localBook) };
    }),
  );
}
